import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { flushSync } from "react-dom";
import { onIdTokenChanged, type User } from "firebase/auth";
import { ensureGuestIdentity, getFirebaseAuth, resumeCredentialedRequests } from "../lib/firebase";
import { afterFirstPaint } from "../../../../shared/lib/afterFirstPaint";
import { signOutSafely } from "./signOutSafely";
import { clearPendingSignOut, finishPendingSignOut, markSignOutFinished, pendingSignOut } from "./deliberateSignOut";
import { signInPathAfterSessionEnd } from "./sessionEndRoute";
import {
  onSessionEnd,
  type SessionEndReason,
  type SessionEndSignal,
} from "../../../../shared/graphql/sessionEnd";
import { resolveClientAccount } from "./clientAccount";
import { forgetProviderName } from "./providerName";
import { noteIdentityChange, useIdleGuestRotation } from "./guestRotation";
import { whenSessionEndAllowed } from "./sessionEndGuard";
import { announceSignOut, noteSignedIn } from "../../../../shared/hooks/useSignOutNotice";

/**
 * Who is using the booking surface right now.
 *
 * THE STATE NAMES ARE THE POINT. `apps/app` models this as
 * `loading | authenticated | anonymous`, where `anonymous` means SIGNED OUT —
 * and Product's guest is signed in *anonymously*, which that model would call
 * `authenticated`. The two vocabularies collide on the same words and mean
 * opposite things.
 *
 * So this surface does not reuse either word:
 *
 *   loading — we do not know yet
 *   guest   — signed in anonymously; can hold a slot and book
 *   account — signed in with a real credential
 *
 * There is no "signed out" state on `book.*` once a booking is under way,
 * because holding a slot requires an identity and we create one on demand.
 *
 * `apps/app` is NOT affected by the guest identity today: it never calls
 * `signInAnonymously`, never reads `isAnonymous`, and sessions are per-origin,
 * so a guest created here is invisible there. Its naming is a latent hazard
 * rather than a live defect — checked rather than assumed, because the estimate
 * had it down as a cross-app refactor reaching every guarded view.
 */
export type IdentityStatus = "loading" | "guest" | "account" | "account-setup" | "account-unavailable";

/** The signed-in client. Deliberately the same shape StepDetails consumes. */
export interface ClientAccount {
  name: string;
  email: string;
}

export interface IdentityValue {
  status: IdentityStatus;
  /** The Firebase user, whichever kind. Null only while loading. */
  user: User | null;
  /**
   * The client's account details, or null for a guest.
   *
   * Null for a guest EVEN THOUGH a guest has a `User` — because the details
   * screen uses this to decide whether it is reading from an account or asking
   * for details, and an anonymous user has neither a name nor an email to read.
   */
  account: ClientAccount | null;
  /**
   * Create the anonymous identity if there is not one yet.
   *
   * Called before a hold or a booking rather than on page load: a client who
   * only looks at a profile should not have an identity minted for them.
   */
  ensureGuest(): Promise<User>;
  /**
   * Resolve the signed-in client's account again, IN PLACE, after it came
   * back `account-unavailable` (c3: the "Try again" there, through
   * retryApiRead). Goes back to `loading`, then to whatever the backend
   * answers now. The same resolution the identity listener runs (network
   * reads, fresh token), for the same user; it never signs anyone in or out.
   * Does nothing unless the status is `account-unavailable`, and nothing for
   * a guest, a signed-out page, an unverified address, or when the user has
   * changed since.
   */
  retryAccount(): void;
  /**
   * Resolve the account AccountCreate just created, IN PLACE (c4, replacing
   * the page reload that followed a committed `account.create`). Goes to
   * `loading`, then to whatever the backend answers now: `account` (Book
   * then routes to service), `account-setup`, or `account-unavailable` with
   * its Try again. The same resolution the identity listener runs.
   *
   * A SEPARATE, GUARDED ENTRY: retryAccount keeps its own guard
   * (`account-unavailable` only, security review H-1). This acts only from
   * `account-setup` (the state the create form is shown in), only for the
   * credentialed, verified user `uid` names, and only while that user is
   * still the current identity. It never signs anyone in or out. Returns
   * false, doing nothing, otherwise; the caller then reloads, the behaviour
   * this replaced.
   */
  resolveCreatedAccount(uid: string): boolean;
}

const IdentityContext = createContext<IdentityValue | null>(null);

export function IdentityProvider({
  children,
  onSessionEnded,
}: {
  children: ReactNode;
  /**
   * Called after a session end has signed the client out, to route them to
   * sign-in. Supplied by main.tsx, which owns the router (this provider sits
   * above it). Never called for a guest.
   */
  onSessionEnded?: (reason: SessionEndReason) => void;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatusState] = useState<IdentityStatus>("loading");
  /**
   * The status as last set, readable outside a render. retryAccount acts
   * only while it is `account-unavailable` (security review H-1): from any
   * other state a retry would drop a live account, or a setup screen, back
   * to loading for nothing. Written only through the identity effect's
   * setStatus, so the two never disagree.
   */
  const statusRef = useRef<IdentityStatus>("loading");
  const [account, setAccount] = useState<ClientAccount | null>(null);
  /** Set by the identity effect below; null outside it. */
  const retryAccountRef = useRef<(() => void) | null>(null);
  /** Set by the identity effect below; null outside it. */
  const createdAccountRef = useRef<((uid: string) => boolean) | null>(null);
  /** Set by the identity effect below: show the page as signed out now. */
  const endLocallyRef = useRef<((uid: string) => void) | null>(null);
  const routeAfterEnd = useRef(onSessionEnded);
  useEffect(() => {
    routeAfterEnd.current = onSessionEnded;
  });

  /*
   * THE ONE OWNER OF A SESSION END ON book.* (shared/graphql/sessionEnd.ts).
   *
   * The Apollo session link raises it for a CREDENTIALED request only: a
   * revoked session (SESSION_REVOKED), or an identity the Firebase SDK
   * refused to refresh. This signs out, then routes to sign-in.
   *
   * Never a guest: an anonymous UID is the booking's clientId, so it is
   * re-checked here as well as at the link. Never a different person: a
   * signal about a UID that is no longer current is dropped.
   *
   * THE SDK MAY HAVE SIGNED OUT FIRST (copy-1). For auth/user-token-expired
   * and auth/user-disabled the Firebase SDK signs the user out itself before
   * the forced refresh rejects, and identity-invalid is raised only after a
   * refresh of the user who was current (shared/graphql/tokenRecovery.ts).
   * So identity-invalid with NOBODY current is this session ending: there is
   * no one left to sign out, and the client is routed to sign-in like any
   * other end (same guard, same notice). Someone current with another UID,
   * or a guest, is still dropped.
   *
   * NEVER WHILE A BOOKING MAY EXIST (auth/sessionEndGuard.ts). While a commit
   * is sending, its outcome is unknown, or its answer is on screen, the
   * sign-out waits; the uncertain screen, its Check and its idempotency key
   * stay exactly as they were. It runs once the booking resolves or the
   * person leaves that screen.
   */
  useEffect(() => {
    let pending: SessionEndSignal | null = null;
    let cancel: (() => void) | null = null;
    const isTheSameCredentialedUser = (signal: SessionEndSignal) => {
      const current = getFirebaseAuth().currentUser;
      if (!current || current.isAnonymous) return false;
      return current.uid === signal.uid;
    };
    const signedOutBySdk = (signal: SessionEndSignal) =>
      signal.reason === "identity-invalid" && getFirebaseAuth().currentUser === null;
    const unsubscribe = onSessionEnd((signal) => {
      if (pending || !(isTheSameCredentialedUser(signal) || signedOutBySdk(signal))) return;
      pending = signal;
      cancel = whenSessionEndAllowed(() => {
        const ending = pending;
        pending = null;
        cancel = null;
        if (!ending) return;
        if (isTheSameCredentialedUser(ending)) {
          // Never stranded (auth/signOutSafely.ts): the person reaches sign-in
          // with the session-ended line even if Firebase refuses the sign-out
          // (a hidden tab, storage pressure), the retry waits for the tab to
          // be visible, and at most one safety reload follows.
          const reloadTo = signInPathAfterSessionEnd(window.location.pathname);
          void signOutSafely({
            uid: ending.uid,
            // Firebase refused (a hidden tab): the SDK still holds the ended
            // user, so the page is told it is signed out until it catches up.
            onRefused: () => endLocallyRef.current?.(ending.uid),
            route: () => routeAfterEnd.current?.(ending.reason),
            reloadTo: reloadTo ?? window.location.pathname,
          });
        } else if (signedOutBySdk(ending)) {
          routeAfterEnd.current?.(ending.reason);
        }
      });
    });
    return () => {
      unsubscribe();
      cancel?.();
    };
  }, []);

  useEffect(() => {
    // NOT BEFORE FIRST PAINT (perf P4). Firebase Auth is first initialized by
    // this subscription, and initializing it starts App Check, whose reCAPTCHA
    // script, dropped by a blocker, would hold WebKit's first paint
    // (lib/firebase.ts `whenAppCheckStarted`). So the listener subscribes
    // after two frames or load; the API's first request waits the same way.

    let version = 0;
    const setStatus = (next: IdentityStatus) => {
      statusRef.current = next;
      setStatusState(next);
    };
    // WHY NOT onAuthStateChanged. Firebase fires that only when the UID
    // changes, and linking an Apple or Google credential to the anonymous
    // guest KEEPS the UID (that is the point of linking: the guest's hold and
    // bookings stay theirs). The guest would then stay `guest` with a
    // credentialed user. onIdTokenChanged fires on the link too, but also on
    // every hourly token refresh, so only a change in WHO this is (the UID, or
    // guest versus credentialed) is re-evaluated. A refresh mid-booking must
    // not drop the flow back to loading.
    let lastIdentity: string | undefined;
    const identityOf = (next: User | null) =>
      next ? `${next.uid}:${next.isAnonymous ? "guest" : "credentialed"}` : "none";

    // One resolution, shared by the listener and retryAccount. `version`
    // drops an answer for a user who is no longer current.
    const resolveAccount = (next: User) => {
      const current = ++version;
      // Cleared first, so a re-resolve that ends unavailable never keeps the
      // details an earlier answer attached.
      setAccount(null);
      setStatus("loading");
      void resolveClientAccount(next).then(
        (resolved) => {
          if (version !== current) return;
          if (resolved.kind === "account") {
            setAccount(resolved.account);
            setStatus("account");
          } else {
            setStatus(resolved.kind === "setup" ? "account-setup" : "account-unavailable");
          }
        },
        () => {
          if (version !== current) return;
          // Never fall back to guest: this Firebase UID is non-anonymous, and
          // the guest checkout is for anonymous identities only.
          setStatus("account-unavailable");
        }
      );
    };

    retryAccountRef.current = () => {
      // Only the account-unavailable Try again may re-resolve.
      if (statusRef.current !== "account-unavailable") return;
      const next = getFirebaseAuth().currentUser;
      if (!next || next.isAnonymous || (!next.emailVerified && next.email)) return;
      if (identityOf(next) !== lastIdentity) return;
      resolveAccount(next);
    };

    endLocallyRef.current = (uid) => {
      // Firebase 1.13 drops the user from memory BEFORE the refused
      // persistence write, and never tells this listener: `currentUser` is
      // usually null here although the page still shows them. Anyone ELSE
      // signed in by now is left alone.
      const current = getFirebaseAuth().currentUser;
      if (current && current.uid !== uid) return;
      // As the listener would on a sign-out; it confirms it (null) once
      // Firebase succeeds, and a reload starts from Firebase's own answer.
      // Committed NOW, before signOutSafely routes: the router renders its
      // navigation at once, and a sign-in step that still read the ended
      // client as signed in would send them straight back into the flow.
      ++version;
      // The listener's view too: signing back in as the same account later
      // (Firebase memory empty, storage refused) is then a sign-in, not a
      // repeat of an identity it believes is current.
      lastIdentity = identityOf(null);
      flushSync(() => {
        setUser(null);
        setAccount(null);
        setStatus("guest");
      });
    };

    createdAccountRef.current = (uid) => {
      // Only right after a create: the form is shown in account-setup. From
      // any other state the listener (or retryAccount) owns the resolution.
      if (statusRef.current !== "account-setup") return false;
      const next = getFirebaseAuth().currentUser;
      // The verified credentialed user who created it, nobody else: a create
      // needs a verified address (createClientAccount), so an unverified or
      // anonymous user here is a different identity.
      if (!next || next.isAnonymous || !next.emailVerified || next.uid !== uid) return false;
      if (identityOf(next) !== lastIdentity) return false;
      resolveAccount(next);
      return true;
    };

    // A credentialed user, as the listener settles them.
    const settleCredentialed = (next: User) => {
      if (!next.emailVerified && next.email) {
        // The email-verification screen precedes account.creationStatus. No
        // create call is possible until Firebase confirms the address.
        setStatus("account-setup");
        return;
      }
      resolveAccount(next);
    };

    /*
     * A SIGN OUT THE LAST LOAD COULD NOT FINISH (auth/deliberateSignOut.ts).
     * The person pressed Sign out, Firebase refused, and the page reloaded
     * once to finish it here, with a fresh SDK, BEFORE the account resolves:
     * nothing of theirs is read or shown first. Signed out: "You're signed
     * out." (the reload already went where they were going). Refused again:
     * they stay signed in, honestly, and their account resolves as usual.
     *
     * Only the page's FIRST identity is the one that was leaving. If that
     * first answer is nobody, the marker stays: "signed out" and "storage
     * unreadable" look the same (Firebase falls back and sees no one), and a
     * later load that sees the stored user finishes it (QE-SO-3). A sign-in
     * made on this page afterwards is someone choosing to be here, and takes
     * the marker away instead.
     */
    let firstIdentitySeen = false;
    const finishingSignOut = (next: User | null): boolean => {
      const first = !firstIdentitySeen;
      firstIdentitySeen = true;
      const signedIn = Boolean(next && !next.isAnonymous);
      if (!first) {
        if (signedIn) clearPendingSignOut();
        return false;
      }
      const pending = pendingSignOut();
      if (!signedIn || !next) {
        // Nobody stored. A finished sign-out has nothing left to watch for;
        // an unfinished one may only be unreadable here, and waits (its time
        // limit bounds that).
        if (pending === "finished") clearPendingSignOut();
        return false;
      }
      if (pending === null) return false;
      const current = version;
      setStatus("loading");
      void finishPendingSignOut(next).then((signedOut) => {
        if (signedOut) {
          // The listener has the null user by now, or will shortly. Kept as
          // "finished": with IndexedDB unwritable, Firebase migrates the
          // stored user to its fallback storage and "signs out" of that,
          // leaving the original record; a later load that finds them
          // restored finishes again (QE-SO-3). The next load that finds
          // nobody, a sign-in on the page, a refusal, or the time limit,
          // takes it.
          markSignOutFinished();
          announceSignOut("signed-out");
          return;
        }
        clearPendingSignOut();
        if (version !== current || getFirebaseAuth().currentUser?.uid !== next.uid) return;
        settleCredentialed(next);
      });
      return true;
    };

    let unsubscribe: (() => void) | null = null;
    const cancelStart = afterFirstPaint(() => {
      unsubscribe = onIdTokenChanged(getFirebaseAuth(), (next) => {
        const identity = identityOf(next);
        if (identity === lastIdentity) return;
        lastIdentity = identity;
        // A provider's name belongs to the sign-in it came with: kept for this
        // UID (a guest that linked keeps its UID), forgotten for anyone else
        // and on sign-out (auth/providerName.ts).
        forgetProviderName(next?.uid ?? null);
        // In the listener, not an effect: Firebase notifies it as the link
        // lands, before a render could, so the stamp is down before another
        // tab can be granted the lock the link's claim was holding (F4).
        noteIdentityChange(next);
        // Signed in (again): an earlier sign-out's notice is no longer news,
        // and a sign-out that never completed (signOutSafely "failed") no
        // longer withholds this identity's token.
        if (next && !next.isAnonymous) {
          noteSignedIn();
          resumeCredentialedRequests();
        }
        ++version;
        setUser(next);
        setAccount(null);
        if (finishingSignOut(next)) return;
        if (!next || next.isAnonymous) {
          setStatus("guest");
          return;
        }
        settleCredentialed(next);
      });
    });
    return () => {
      ++version;
      retryAccountRef.current = null;
      createdAccountRef.current = null;
      endLocallyRef.current = null;
      cancelStart();
      unsubscribe?.();
    };
  }, []);

  // An anonymous identity nobody is using is retired after the idle limit, so
  // the next person at a shared device never inherits it (security audit M1).
  useIdleGuestRotation(user);

  const ensureGuest = useCallback(async () => ensureGuestIdentity(), []);
  const retryAccount = useCallback(() => retryAccountRef.current?.(), []);
  const resolveCreatedAccount = useCallback((uid: string) => createdAccountRef.current?.(uid) ?? false, []);

  const value = useMemo<IdentityValue>(
    () => ({ status, user, account, ensureGuest, retryAccount, resolveCreatedAccount }),
    [status, user, account, ensureGuest, retryAccount, resolveCreatedAccount]
  );

  return <IdentityContext.Provider value={value}>{children}</IdentityContext.Provider>;
}

export function useIdentity(): IdentityValue {
  const value = useContext(IdentityContext);
  if (!value) throw new Error("useIdentity must be used inside an IdentityProvider");
  return value;
}
